"use client";

import { BrowserStorageIdsEnum, useDidUpdateEffect } from "@finbackoffice/site-core";
import { HoursRange } from "@finbackoffice/websocket-client";
import { useDate } from "hooks";
import {
    Dispatch,
    FC,
    PropsWithChildren,
    SetStateAction,
    createContext,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

const convertToLocalStorageItems = (tournaments: ITournamentIdData[]) => {
    return tournaments.map((tournament) => ({
        ...tournament,
    }));
};

export interface ITournamentIdData {
    sportId: number;
    categoryId: number;
    tournamentId: number;
}

type CalendarDateState = {
    from: number;
    to: number;
};

type IPrematchContext = {
    activeCalendarDate: CalendarDateState | null;
    calendarDateOptions: number[];
    tournaments: ITournamentIdData[];
    hoursRange: HoursRange;
    gameDoesNotExist: boolean;
    setHoursRange: Dispatch<SetStateAction<HoursRange>>;
    resetHoursRange: () => void;
    setGameDoesNotExist: Dispatch<SetStateAction<boolean>>;
    setActiveCalendarDate: (timestamp: number) => void;
    addTournament: (payload: ITournamentIdData) => void;
    removeTournament: (id: number) => void;
    changeTournament: (payload: ITournamentIdData) => void;
    clearTournaments: () => void;
    isCategorySelected: (categoryId: number) => boolean;
    isSportSelected: (sportId: number) => boolean;
    isTournamentSelected: (tournamentId: number) => boolean;
};

export const PrematchContext = createContext<IPrematchContext>(null as any);

export const PrematchProvider: FC<PropsWithChildren> = ({ children }) => {
    const { getTime, endOfDay, addDays, differenceInDays, startOfDay } = useDate();
    const [tournaments, setTournaments] = useState<ITournamentIdData[]>([]);
    const [hoursRange, setHoursRange] = useState<HoursRange>(HoursRange.ALL);
    const [gameDoesNotExist, setGameDoesNotExist] = useState(false);

    useEffect(() => {
        const storageTournaments =
            sessionStorage.getItem(BrowserStorageIdsEnum.PREMATCH_TOURNAMENTS) ?? "[]";
        const parsedArr: ITournamentIdData[] = JSON.parse(storageTournaments);
        if (parsedArr.length > 1) {
            setTournaments(parsedArr);
        }
    }, []);

    useDidUpdateEffect(() => {
        if (tournaments.length > 1) {
            sessionStorage.setItem(
                BrowserStorageIdsEnum.PREMATCH_TOURNAMENTS,
                JSON.stringify(convertToLocalStorageItems(tournaments)),
            );
        } else {
            sessionStorage.removeItem(BrowserStorageIdsEnum.PREMATCH_TOURNAMENTS);
        }
    }, [tournaments.length]);

    const calendarDateOptions: number[] = useMemo(() => {
        const result: number[] = [];
        const calendarDateRange = {
            from: getTime(addDays(endOfDay(new Date()), 1)),
            to: getTime(addDays(endOfDay(new Date()), 3)),
        };

        for (let i = 0; i < -differenceInDays(calendarDateRange.from, calendarDateRange.to); ++i) {
            const day = addDays(calendarDateRange.from, i);

            result.push(day.valueOf());
        }

        return result;
    }, [addDays, differenceInDays, endOfDay, getTime]);
    const [activeCalendarDate, setActiveCalendarDate] = useState<CalendarDateState | null>({
        from: getTime(startOfDay(calendarDateOptions[0])),
        to: getTime(endOfDay(calendarDateOptions[0])),
    });

    const updateActiveCalendarDate = useCallback(
        (timestamp: number) => {
            setActiveCalendarDate({
                from: getTime(startOfDay(timestamp)),
                to: getTime(endOfDay(timestamp)),
            });
        },
        [endOfDay, getTime, startOfDay],
    );

    const addTournament = useCallback((payload: ITournamentIdData) => {
        const { sportId, categoryId, tournamentId } = payload;

        setTournaments((state) => [...state, { sportId, categoryId, tournamentId }]);
    }, []);

    const removeTournament = useCallback((id: number) => {
        setTournaments((state) => state.filter((tournament) => tournament.tournamentId !== id));
    }, []);

    const changeTournament = useCallback((payload: ITournamentIdData) => {
        const { sportId, categoryId, tournamentId } = payload;
        setTournaments([{ sportId, categoryId, tournamentId }]);
    }, []);

    const clearTournaments = useCallback(() => {
        setTournaments([]);
    }, []);

    const isCategorySelected = useCallback(
        (categoryId: number) =>
            tournaments?.find((tournament) => tournament.categoryId === categoryId) !== undefined,
        [tournaments],
    );

    const isSportSelected = useCallback(
        (sportId: number) =>
            tournaments.find((tournament) => tournament.sportId === sportId) !== undefined,
        [tournaments],
    );

    const isTournamentSelected = useCallback(
        (tournamentId: number) =>
            tournaments.find((item) => tournamentId === item.tournamentId) !== undefined,
        [tournaments],
    );

    const resetHoursRange = useCallback(() => {
        if (hoursRange !== HoursRange.ALL) {
            setHoursRange(HoursRange.ALL);
        }
    }, [hoursRange]);

    useDidUpdateEffect(() => {
        clearTournaments();
    }, [hoursRange]);

    const value = useMemo(
        () => ({
            hoursRange,
            tournaments,
            gameDoesNotExist,
            activeCalendarDate,
            calendarDateOptions,
            setHoursRange,
            resetHoursRange,
            setGameDoesNotExist,
            setActiveCalendarDate: updateActiveCalendarDate,
            addTournament,
            removeTournament,
            changeTournament,
            clearTournaments,
            isCategorySelected,
            isSportSelected,
            isTournamentSelected,
        }),
        [
            hoursRange,
            tournaments,
            gameDoesNotExist,
            activeCalendarDate,
            calendarDateOptions,
            resetHoursRange,
            updateActiveCalendarDate,
            addTournament,
            removeTournament,
            changeTournament,
            clearTournaments,
            isCategorySelected,
            isSportSelected,
            isTournamentSelected,
        ],
    );

    return <PrematchContext.Provider value={value}>{children}</PrematchContext.Provider>;
};
